<template>
  <div>
    <UIProgressSpinner v-if="loadingSdk" color="current" height="30" width="30" class="mx-auto" />
    <UIBaseTransition v-show="!loadingSdk">
      <UIProgressSpinner v-if="loading" color="current" height="30" width="30" class="mx-auto" />
      <div v-else ref="paypalButtons" class="paypal-buttons-container"></div>
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import { loadScript } from '@paypal/paypal-js'
import { POPUPS } from '../dynamic/maps'
import { ACTIVE_CAMPAIGN_TAGS_IDS } from '~/constants/index'

const CURRENCY = 'USD'

const props = defineProps<{
  amount?: number
  packageId?: string
  paypalPlanId?: string
}>()

const paymentAmount = computed(() => props.amount ?? 0)
const isSubscription = computed(() => !!props.paypalPlanId)

const uiStore = useUIStore()
const authStore = useAuthStore()
const { sendEventToBackend } = useTracking()

const emits = defineEmits(['close'])

const { mutateAsync: captureOrder } = useCapturePaypalOrder()
const { mutateAsync: activateSubscription } = useActivatePaypalSubscription()

const { mutateAsync: addTag } = useSendActiveCampaignTag()

const { refetch: refetchUser } = useCurrentUser()

const { refetch: refetchPackages } = usePackages()

const { refetch: refetchAttemptedPackage } = useAttemptedPackage()

const { refetch: refetchDeclineStatus } = useDeclineStatus()

const { catchErrors } = useErrorHandler()

const config = useRuntimeConfig()
const { locale } = useI18n()

const paypalButtons = ref<HTMLElement | null>(null)
const loading = ref(false)
const loadingSdk = ref(true)

const getPayPalLocale = (appLocale: string) => {
  const localeMap: Record<string, string> = {
    en: 'en_US',
    de: 'de_DE',
  }
  return localeMap[appLocale] || 'en_US'
}

const sendPaymentEvent = (eventName: string, errorMessage?: string) => {
  const eventData: Record<string, any> = {
    crm_user_id: authStore.getUser?.id,
    email: authStore.getUser?.email,
    phone: authStore.getUser?.phone,
    fname: authStore.getUser?.firstName,
    lname: authStore.getUser?.lastName,
    amount: paymentAmount.value,
    currency: CURRENCY,
    payment_method: 'paypal',
    package_id: props.packageId,
    is_subscription: isSubscription.value,
  }
  if (errorMessage) {
    eventData.error_message = errorMessage
  }
  sendEventToBackend(eventName, eventData)
}

const handlePaymentSuccess = async () => {
  loading.value = false
  emits('close')
  await Promise.all([refetchUser(), refetchPackages()])
  sendPaymentEvent('payment_success')
  nextTick(() => {
    uiStore.showPopup(POPUPS.PAYMENT_SUCCESS)
  })
}

const handlePaymentFailure = async (errorMessage: string) => {
  loading.value = false
  sendPaymentEvent('payment_failed', errorMessage)
  addTag(ACTIVE_CAMPAIGN_TAGS_IDS.PAYMENT_FAILED).catch(() => {})
  emits('close')
  await Promise.all([refetchAttemptedPackage(), refetchDeclineStatus()])
  nextTick(() => {
    uiStore.showPopup(POPUPS.PAYMENT_FAILED)
  })
}

onMounted(async () => {
  try {
    const paypal = await loadScript({
      clientId: config.public.paypalClientId,
      locale: getPayPalLocale(locale.value),
      intent: isSubscription.value ? 'subscription' : 'capture',
      vault: isSubscription.value ? true : undefined,
    })

    if (paypal && typeof paypal.Buttons === 'function') {
      loadingSdk.value = false

      await new Promise((resolve) => setTimeout(resolve, 250))

      if (paypalButtons.value && paypal.FUNDING) {
        const buttonConfig: any = {
          fundingSource: paypal.FUNDING.PAYPAL,
          style: {
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: isSubscription.value ? 'subscribe' : 'pay',
            height: 48,
          },
          onError: (error: any) => {
            loading.value = false
            catchErrors(error)
          },
          onCancel: async () => {
            await handlePaymentFailure('Payment cancelled by user')
          },
        }

        if (isSubscription.value) {
          buttonConfig.createSubscription = (_data: any, actions: any) => {
            sendPaymentEvent('payment_attempt')
            return actions.subscription.create({
              plan_id: props.paypalPlanId,
              custom_id: JSON.stringify({
                userId: authStore.getUser?.id,
                packageId: props.packageId,
              }),
              application_context: {
                shipping_preference: 'NO_SHIPPING',
                user_action: 'SUBSCRIBE_NOW',
                brand_name: 'Dubai Insiders Club',
              },
            })
          }

          buttonConfig.onApprove = async (data: any) => {
            loading.value = true
            try {
              const subscriptionId = data.subscriptionID
              if (!subscriptionId) {
                throw new Error('Subscription ID is missing')
              }
              await activateSubscription({
                subscriptionId,
                packageId: props.packageId,
              })
              await handlePaymentSuccess()
            } catch (error: unknown) {
              console.error('Error activating subscription:', error)
              catchErrors(error)
              const errorMessage = error instanceof Error ? error.message : 'Unknown error'
              await handlePaymentFailure(errorMessage)
            }
          }
        } else {
          buttonConfig.createOrder = (_data: any, actions: any) => {
            sendPaymentEvent('payment_attempt')
            return actions.order.create({
              intent: 'CAPTURE',
              payer: {
                email_address: authStore.getUser?.email || '',
              },
              application_context: {
                shipping_preference: 'NO_SHIPPING',
                user_action: 'PAY_NOW',
                brand_name: 'Dubai Insiders Club',
              },
              purchase_units: [
                {
                  amount: {
                    value: paymentAmount.value.toFixed(2),
                    currency_code: CURRENCY,
                  },
                },
              ],
            })
          }

          buttonConfig.onApprove = async (_data: any, actions: any) => {
            loading.value = true
            try {
              if (!actions.order || typeof actions.order.capture !== 'function') {
                throw new Error('PayPal order capture function not available')
              }

              const orderId = _data.orderID || null

              if (orderId) {
                try {
                  await captureOrder({ orderId, packageId: props.packageId })
                  await handlePaymentSuccess()
                  return
                } catch {
                  console.log('Server-side capture with approval ID failed, trying client-side capture')
                }
              }

              try {
                const order = await actions.order.capture()
                const finalOrderId = order?.id
                if (!finalOrderId) {
                  throw new Error('Order capture failed or order ID is missing.')
                }
                await captureOrder({ orderId: finalOrderId, packageId: props.packageId })
                await handlePaymentSuccess()
              } catch (captureError: unknown) {
                console.error('Client-side capture error:', captureError)
                const errorMessage = captureError instanceof Error ? captureError.message : ''
                if (orderId && errorMessage.includes('Window closed before response')) {
                  await captureOrder({ orderId, packageId: props.packageId })
                  await handlePaymentSuccess()
                } else {
                  throw captureError
                }
              }
            } catch (error: unknown) {
              console.error('Error capturing order:', error)
              catchErrors(error)
              const errorMessage = error instanceof Error ? error.message : 'Unknown error'
              await handlePaymentFailure(errorMessage)
            }
          }
        }

        paypal
          .Buttons(buttonConfig)
          .render(paypalButtons.value)
          .catch((renderError) => {
            console.error('PayPal button render error:', renderError)
            catchErrors(renderError)
          })
      } else {
        catchErrors(new Error('PayPal container element not found after transition.'))
      }
    } else {
      loadingSdk.value = false
      catchErrors(new Error('PayPal SDK failed to load.'))
    }
  } catch (error) {
    loading.value = false
    loadingSdk.value = false
    catchErrors(error)
  }
})
</script>

<style scoped>
.paypal-buttons-container {
  --button-height: 48px;
  border-radius: 12px;
  overflow: hidden;
  height: var(--button-height);
}

.paypal-buttons-container :deep(> div) {
  height: var(--button-height) !important;
}

.paypal-buttons-container :deep(iframe) {
  height: var(--button-height) !important;
}
</style>
